// NOTICE: KAPTION-INTEGRATION
// whatsapp-call-transcripts.WEB.T1..T7 — the transcript of a Cloud API call,
// as the reader needs it. Pure functions, no Vue: normalising the API shape,
// pairing each turn's text with its timed words, finding the turn and word
// under the playhead, inline markers (handover, hold, consult, notice), find
// in transcript, and the plain-text export.
//
// Ported from the desktop reader (kaptionai desktop/shell/rail.js wordHtml,
// approximateWords, highlightTurn, highlightWord — kaptionai desktop-shell spec CALL 14) and
// the extension (kext callRecording/ext/transcriptWords.ts, EXT.7), so the
// three readers light, hold and seek the same way.
//
// API shape (GET kaption/calls/:id/transcript, whatsapp-call-transcripts
// API.1). Every field name is read in ONE place — normalizeTranscript — so a
// rename on the Rails side is a one-line change here:
//   {status, language, duration_ms, words_coverage,
//    speakers: [{key: 'customer'|'user:12', user_id, name}],
//    turns: [{speaker, start, end, text, words: [[text, start, end, prob]],
//             approximate?}],
//    markers: [{t, kind: 'handover'|'hold_on'|'hold_off'|'consult'|'notice'|
//                     'gap'|'recording_stopped'|'recording_resumed',
//               from_user_id, to_user_id, note, user_ids (consult), end}]}
// Times are seconds from the start of the recording. `status` is
// 'not_recorded' when the call had no recording at all; 'skipped' carries
// its reason in `error` (not_recorded | transcription_disabled |
// notice_not_played). Holds come only as hold_on/hold_off markers — the call
// JSON has no holds[].

export const TRANSCRIPT_STATUS = Object.freeze({
  PENDING: 'pending',
  PROCESSING: 'processing',
  READY: 'ready',
  PARTIAL: 'partial',
  FAILED: 'failed',
  SKIPPED: 'skipped',
  PURGED: 'purged',
  NOT_RECORDED: 'not_recorded',
});

// Statuses whose turns can be read.
export const READABLE_STATUSES = [
  TRANSCRIPT_STATUS.READY,
  TRANSCRIPT_STATUS.PARTIAL,
];
// Statuses still on their way (spinner; a live event will move them on).
export const WORKING_STATUSES = [
  TRANSCRIPT_STATUS.PENDING,
  TRANSCRIPT_STATUS.PROCESSING,
];

export const MARKER_KIND = Object.freeze({
  HANDOVER: 'handover',
  HOLD_ON: 'hold_on',
  HOLD_OFF: 'hold_off',
  CONSULT: 'consult',
  NOTICE: 'notice',
  GAP: 'gap',
  RECORDING_STOPPED: 'recording_stopped',
  RECORDING_RESUMED: 'recording_resumed',
});

export const CUSTOMER_KEY = 'customer';

// One colour per agent, in order of first appearance. Never orange (the
// Transcripts accent) or red (Recording) — style.kaption.us/STYLE.md.
export const AGENT_TONES = [
  { text: 'text-n-blue-11', bar: 'bg-n-blue-9', soft: 'bg-n-blue-3' },
  { text: 'text-n-teal-11', bar: 'bg-n-teal-9', soft: 'bg-n-teal-3' },
  { text: 'text-n-iris-11', bar: 'bg-n-iris-9', soft: 'bg-n-iris-3' },
  { text: 'text-n-green-11', bar: 'bg-n-green-9', soft: 'bg-n-green-3' },
];
export const CUSTOMER_TONE = {
  text: 'text-n-slate-11',
  bar: 'bg-n-slate-9',
  soft: 'bg-n-alpha-2',
};

// The desktop's constants (rail.js highlightTurn / highlightWord).
export const WORD_HOLD_SECONDS = 0.35;
const START_SLACK = 0.02;
const TURN_SLACK = 0.05;

const num = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const pick = (...values) => values.find(v => v !== undefined && v !== null);

// [text, start, end, prob] (compact jsonb) or {text, start, end, probability}.
export const normalizeWord = raw => {
  if (Array.isArray(raw)) {
    const [text, start, end, probability] = raw;
    return {
      text: String(text ?? ''),
      start: num(start),
      end: num(end),
      probability: num(probability),
    };
  }
  if (!raw || typeof raw !== 'object') return null;
  return {
    text: String(raw.text ?? raw.word ?? ''),
    start: num(raw.start),
    end: num(raw.end),
    probability: num(pick(raw.probability, raw.prob, raw.p)),
  };
};

const userIdOfKey = key => {
  const match = /^user:(\d+)$/.exec(String(key || ''));
  return match ? Number(match[1]) : null;
};

export const normalizeTranscript = (raw, { customerName = '' } = {}) => {
  if (!raw || typeof raw !== 'object') return null;
  const data =
    raw.transcript && typeof raw.transcript === 'object' ? raw.transcript : raw;
  const status = pick(
    data.status,
    data.transcript_status,
    TRANSCRIPT_STATUS.PENDING
  );

  // Speakers: listed ones first, then any key a turn uses that the list lacks.
  const speakers = [];
  const byKey = new Map();
  const addSpeaker = (key, extra = {}) => {
    if (!key || byKey.has(key)) return byKey.get(key);
    const isCustomer = key === CUSTOMER_KEY || key === 'contact';
    const userId = isCustomer
      ? null
      : pick(extra.user_id, extra.userId, userIdOfKey(key));
    const agentIndex = isCustomer
      ? -1
      : speakers.filter(s => !s.isCustomer).length;
    const speaker = {
      key,
      isCustomer,
      userId,
      name: pick(extra.name, isCustomer ? customerName : '', ''),
      tone: isCustomer
        ? CUSTOMER_TONE
        : AGENT_TONES[agentIndex % AGENT_TONES.length],
      agentIndex,
    };
    speakers.push(speaker);
    byKey.set(key, speaker);
    return speaker;
  };
  (Array.isArray(data.speakers) ? data.speakers : []).forEach(s =>
    addSpeaker(s?.key, s || {})
  );

  const turns = (Array.isArray(data.turns) ? data.turns : [])
    .map((turn, index) => {
      const speakerKey = String(
        pick(turn?.speaker, turn?.speaker_key, CUSTOMER_KEY)
      );
      addSpeaker(speakerKey);
      return {
        index,
        speaker: speakerKey,
        start: num(turn?.start) ?? 0,
        end: num(turn?.end) ?? num(turn?.start) ?? 0,
        text: String(turn?.text ?? '')
          .replace(/\s+/g, ' ')
          .trim(),
        words: (Array.isArray(turn?.words) ? turn.words : [])
          .map(normalizeWord)
          .filter(Boolean),
        approximate: !!turn?.approximate,
      };
    })
    .filter(turn => turn.text);

  const markers = (Array.isArray(data.markers) ? data.markers : [])
    .map(marker => ({
      t: num(pick(marker?.t, marker?.at)) ?? 0,
      kind: String(marker?.kind || ''),
      fromUserId: pick(marker?.from_user_id, marker?.fromUserId, null),
      toUserId: pick(marker?.to_user_id, marker?.toUserId, null),
      note: pick(marker?.note, '') || '',
      userIds: (Array.isArray(marker?.user_ids) ? marker.user_ids : [])
        .map(Number)
        .filter(Number.isFinite),
      end: num(marker?.end),
      durationSeconds: num(
        pick(marker?.duration_seconds, marker?.durationSeconds)
      ),
    }))
    .filter(marker => marker.kind)
    .sort((a, b) => a.t - b.t);

  const durationMs = num(pick(data.duration_ms, data.durationMs));
  const lastEnd = turns.reduce((max, turn) => Math.max(max, turn.end), 0);
  return {
    status,
    language: pick(data.language, ''),
    durationSeconds: durationMs !== null ? durationMs / 1000 : lastEnd,
    wordsCoverage: num(pick(data.words_coverage, data.wordsCoverage)),
    speakers,
    turns,
    markers,
    // Header facts — optional; the reader hides what is missing.
    consent: pick(data.consent, data.recording_consent, null),
    retentionDays: num(pick(data.retention_days, data.retentionDays)),
    purgedAt: pick(data.purged_at, data.purgedAt, null),
    error: pick(data.error, null),
    permissions: {
      listen: pick(data.permissions?.listen, data.can_listen, null),
      download: pick(data.permissions?.download, data.can_download, null),
      retry: pick(data.permissions?.retry, data.can_retry, null),
    },
  };
};

// A speaker's display name: the transcript's own, else the call's legs, else
// a generic label (the caller passes the translated fallbacks).
export const speakerLabel = (speaker, { customer = '', agent = '' } = {}) => {
  if (!speaker) return agent;
  if (speaker.isCustomer) return speaker.name || customer;
  return speaker.name || agent;
};

export const firstName = name => String(name || '').split(' ')[0] || '';

/**
 * kaptionai desktop-shell spec CALL 14 approximateWords — a word whose placement is not to be
 * trusted: one inside a run of five whose mean confidence is under 0.2 (what a
 * lost aligner looks like). Words with no confidence count as exact.
 */
export const approximateWordRuns = (words, windowSize = 5, threshold = 0.2) => {
  const flags = new Array(words.length).fill(false);
  if (words.length < windowSize) return flags;
  for (let start = 0; start + windowSize <= words.length; start += 1) {
    let sum = 0;
    for (let i = start; i < start + windowSize; i += 1)
      sum += words[i].probability ?? 1;
    if (sum / windowSize < threshold)
      for (let i = start; i < start + windowSize; i += 1) flags[i] = true;
  }
  return flags;
};

const normToken = value =>
  String(value)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '');

/**
 * whatsapp-call-transcripts.WEB.T2 — a turn's text as tokens, each paired BY
 * TEXT (forward only, look-ahead 3) with its timed word: the text and the
 * aligner come from the same engine but not the same pass, so an index walk
 * would throw timings away. Under 60% agreement the turn has no word timings
 * (it lights as a whole). Words are clamped into their turn: a first word
 * after a silence claims the silence.
 *
 * Returns {tokens: [{text, at, start?, end?, approx?}], timed: boolean}.
 */
export const pairTurnWords = turn => {
  const tokens = Array.from(
    String(turn.text || '').matchAll(/\S+/g),
    match => ({
      text: match[0],
      at: match.index ?? 0,
    })
  );
  const words = turn.words || [];
  if (!words.length) return { tokens, timed: false };
  const approx = approximateWordRuns(words);
  const timings = new Array(tokens.length).fill(null);
  let placed = 0;
  let next = 0;
  const inTurn =
    Number.isFinite(turn.start) &&
    Number.isFinite(turn.end) &&
    turn.end >= turn.start;
  for (let i = 0; i < tokens.length && next < words.length; i += 1) {
    const target = normToken(tokens[i].text);
    if (target) {
      for (let k = next; k < Math.min(words.length, next + 3); k += 1) {
        if (normToken(words[k].text) === target) {
          let { start, end } = words[k];
          if (start === null || end === null) return { tokens, timed: false };
          if (inTurn) {
            start = Math.min(Math.max(start, turn.start), turn.end);
            end = Math.min(Math.max(end, start), turn.end);
          }
          timings[i] = { start, end, approx: approx[k] || turn.approximate };
          next = k + 1;
          placed += 1;
          break;
        }
      }
    }
  }
  if (placed < Math.ceil(tokens.length * 0.6)) return { tokens, timed: false };
  timings.forEach((timing, i) => {
    if (timing) Object.assign(tokens[i], timing);
  });
  return { tokens, timed: true };
};

/**
 * kaptionai desktop-shell spec CALL 14 highlightWord — the token being spoken: the last one
 * started (binary search), held through the 0.35 s gap after it. An
 * approximate word holds nothing. Returns its index or -1.
 */
export const wordAt = (tokens, seconds) => {
  const timed = [];
  tokens.forEach((token, i) => {
    if (token.start !== undefined) timed.push(i);
  });
  let lo = 0;
  let hi = timed.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (tokens[timed[mid]].start <= seconds + START_SLACK) {
      found = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  if (found < 0) return -1;
  const token = tokens[timed[found]];
  return !token.approx && seconds <= token.end + WORD_HOLD_SECONDS
    ? timed[found]
    : -1;
};

/**
 * kaptionai desktop-shell spec CALL 14 highlightTurn — the turn being spoken. Both sides talk
 * over each other, so turns overlap:
 *  1. a turn whose timed WORDS cover the playhead (the last such one);
 *  2. else the shortest turn that contains it;
 *  3. else the last turn that started.
 * `rows` = [{turn, tokens, timed}].
 */
export const turnAt = (rows, seconds) => {
  let index = -1;
  rows.forEach((row, i) => {
    if (!row.timed) return;
    const timedTokens = row.tokens.filter(t => t.start !== undefined);
    if (!timedTokens.length) return;
    const first = timedTokens[0].start;
    const last = timedTokens[timedTokens.length - 1].end;
    if (
      last > first &&
      first <= seconds + TURN_SLACK &&
      seconds <= last + WORD_HOLD_SECONDS
    )
      index = i;
  });
  if (index >= 0) return index;
  let shortest = Infinity;
  rows.forEach(({ turn }, i) => {
    if (!(turn.end > turn.start)) return;
    if (turn.start > seconds + TURN_SLACK || seconds > turn.end + TURN_SLACK)
      return;
    if (turn.end - turn.start < shortest) {
      shortest = turn.end - turn.start;
      index = i;
    }
  });
  if (index >= 0) return index;
  rows.forEach(({ turn }, i) => {
    if (turn.start <= seconds + TURN_SLACK) index = i;
  });
  return index;
};

// whatsapp-call-transcripts.WEB.T4 — hold spans from hold_on/hold_off pairs
// (an open hold runs to the end).
export const holdSpans = (markers, durationSeconds = 0) => {
  const spans = [];
  let open = null;
  markers.forEach(marker => {
    if (marker.kind === MARKER_KIND.HOLD_ON && open === null) open = marker.t;
    if (marker.kind === MARKER_KIND.HOLD_OFF && open !== null) {
      spans.push({ start: open, end: marker.t });
      open = null;
    }
  });
  if (open !== null)
    spans.push({ start: open, end: Math.max(open, durationSeconds) });
  return spans;
};

// The inline markers the reader shows between turns. hold_on carries the
// hold's length; hold_off folds into it.
export const displayMarkers = (markers, durationSeconds = 0) => {
  const spans = holdSpans(markers, durationSeconds);
  let hold = 0;
  return markers
    .filter(marker => marker.kind !== MARKER_KIND.HOLD_OFF)
    .map(marker => {
      if (marker.kind !== MARKER_KIND.HOLD_ON) return marker;
      const span = spans[hold];
      hold += 1;
      return {
        ...marker,
        durationSeconds:
          marker.durationSeconds ??
          (span ? Math.max(0, span.end - span.start) : null),
      };
    });
};

// Turns and markers in reading order: a marker sits before the first turn
// that starts at or after it.
export const readingOrder = (rows, markers) => {
  const items = [];
  let m = 0;
  rows.forEach((row, rowIndex) => {
    while (m < markers.length && markers[m].t <= row.turn.start + TURN_SLACK) {
      items.push({ type: 'marker', key: `m${m}`, marker: markers[m] });
      m += 1;
    }
    items.push({ type: 'turn', key: `t${row.turn.index}`, row, rowIndex });
  });
  while (m < markers.length) {
    items.push({ type: 'marker', key: `m${m}`, marker: markers[m] });
    m += 1;
  }
  return items;
};

// whatsapp-call-transcripts.WEB.T7 — find in transcript. Case- and
// accent-insensitive ("transferencia" finds "Transferencia", "cuanto" finds
// "cuánto"). Returns [{rowIndex, from, to}] over each turn's text.
const fold = value =>
  String(value).normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

export const foldText = fold;

// call-transcript-search.WEB.5 — the query's terms: quoted phrases stay
// whole, everything else splits on spaces; at most 8, folded.
export const queryTerms = query => {
  const source = String(query || '').slice(0, 200);
  return Array.from(source.matchAll(/"([^"]+)"|(\S+)/g), match =>
    fold(match[1] || match[2] || '')
      .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  )
    .filter(Boolean)
    .slice(0, 8);
};

const isWordChar = ch => !!ch && /[\p{L}\p{N}]/u.test(ch);

// Every term, matched at the start of a word (the server's prefix rule), in
// one turn's text. Returns [[from, to]] sorted, non-overlapping. NFD +
// dropping marks keeps one char per char for Latin text, so the offsets line
// up with the original.
export const marksIn = (text, terms) => {
  const haystack = fold(text);
  const marks = [];
  terms.forEach(term => {
    let at = haystack.indexOf(term);
    while (at !== -1) {
      if (!isWordChar(haystack[at - 1])) marks.push([at, at + term.length]);
      at = haystack.indexOf(term, at + 1);
    }
  });
  marks.sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  return marks.reduce((kept, mark) => {
    const last = kept[kept.length - 1];
    if (!last || mark[0] >= last[1]) kept.push(mark);
    return kept;
  }, []);
};

export const findMatches = (rows, query) => {
  const terms = queryTerms(query);
  if (!terms.length) return [];
  const matches = [];
  rows.forEach((row, rowIndex) => {
    marksIn(row.turn.text, terms).forEach(([from, to]) =>
      matches.push({ rowIndex, from, to })
    );
  });
  return matches;
};

// One token split into marked / unmarked pieces. `ranges` are [from, to,
// isCurrent] over the whole turn's text.
export const tokenPieces = (token, ranges) => {
  const local = [];
  ranges.forEach(([from, to, current]) => {
    const a = Math.max(from, token.at);
    const b = Math.min(to, token.at + token.text.length);
    if (b > a) local.push([a - token.at, b - token.at, current]);
  });
  if (!local.length) return [{ text: token.text, mark: false }];
  const pieces = [];
  let cursor = 0;
  local.forEach(([a, b, current]) => {
    if (a > cursor)
      pieces.push({ text: token.text.slice(cursor, a), mark: false });
    pieces.push({
      text: token.text.slice(a, b),
      mark: true,
      current: !!current,
    });
    cursor = b;
  });
  if (cursor < token.text.length)
    pieces.push({ text: token.text.slice(cursor), mark: false });
  return pieces;
};

export const formatClock = seconds => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = n => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
};

// whatsapp-call-transcripts.WEB.T7 — Copy / Download .txt. `labels` carries
// the translated pieces so this stays free of i18n.
export const transcriptPlainText = (
  transcript,
  { title = '', names, markerText }
) => {
  const rows = transcript.turns.map(turn => ({ turn }));
  const markers = displayMarkers(
    transcript.markers,
    transcript.durationSeconds
  );
  const lines = title ? [title, ''] : [];
  readingOrder(rows, markers).forEach(item => {
    if (item.type === 'marker') {
      const text = markerText(item.marker);
      if (text) lines.push(`[${formatClock(item.marker.t)}] — ${text}`);
      return;
    }
    const { turn } = item.row;
    lines.push(
      `[${formatClock(turn.start)}] ${names(turn.speaker)}: ${turn.text}`
    );
  });
  return `${lines.join('\n')}\n`;
};
